import {includes} from 'lodash';
import memoize from 'memoize-one';

import type {BulkEditorProjectTagsData} from '../../state/graphql/runSelectorQuery';
import {RunSetPart} from '../../state/views/runSet/types';
import {Config as TableSettings} from '../../util/runfeed';
import * as Run from '../../util/runs';
import * as RunTypes from '../../util/runTypes';
import * as TableCols from '../../util/tablecols';
import {DisabledFeaturesMap, RunSelectorFeature} from './types';
import {WBTableColumn, WBTableRowFields} from './WBTable/types';

// basically a strict readonly where the run set is fully locked;
// user can only view list of runs with no other interactions
const RUN_SELECTOR_DISABLE_FEATURES_ALL: Record<RunSelectorFeature, true> = {
  'auto-cols': true,
  'create-sweep': true,
  delete: true,
  'edit-cols': true,
  'edit-run-name': true,
  'edit-run-notes': true,
  export: true,
  filter: true,
  group: true,
  move: true,
  'push-to-run-queue': true,
  search: true,
  sort: true,
  'stop-run': true,
  tag: true,
  'toggle-visibility': true,
};

// when user is viewing a workspace where they only have read access for the project
export const PROJECT_READONLY_WORKSPACE_RUN_SELECTOR_DISABLED_FEATURES: DisabledFeaturesMap =
  {
    'create-sweep': true,
    delete: true,
    'edit-run-name': true,
    'edit-run-notes': true,
    move: true,
    'stop-run': true,
    tag: true,
  };

export const PUBLISHED_REPORT_RUN_SELECTOR_DISABLED_FEATURES: DisabledFeaturesMap =
  {
    ...RUN_SELECTOR_DISABLE_FEATURES_ALL,
    'toggle-visibility': false,
  };

export const CREATE_SWEEP_PAGE_RUN_SELECTOR_DISABLED_FEATURES: DisabledFeaturesMap =
  {
    'create-sweep': true,
    delete: true,
    'edit-run-name': true,
    'stop-run': true,
    'toggle-visibility': true,
  };

export const SWEEP_PRIOR_RUNS_DISABLED_FEATURES: DisabledFeaturesMap =
  RUN_SELECTOR_DISABLE_FEATURES_ALL;

type RunToGetRow = {name: string};

function getRows<T extends RunToGetRow>(
  runs: T[],
  selectedRunName: string | undefined,
  runSet: RunSetPart
): WBTableRowFields<
  T & {
    runsetInfo: {id: string; name: string};
    selectedRunName: string | undefined;
  }
>[] {
  return runs.map(r => {
    return {
      ...r,
      selectedRunName,
      runsetInfo: {
        id: runSet.id,
        name: runSet.name,
      },
      __address__: TableCols.makeRowAddressComponent(r.name, runSet.id),
    };
  });
}

function getColumns(
  filteredKeys: RunTypes.Key[],
  defaultKeys: RunTypes.Key[],
  tableSettings: TableSettings,
  renderHeader: WBTableColumn['renderHeader'],
  renderCell: WBTableColumn['renderCell']
) {
  const filteredKeyStrings = filteredKeys.map(k => Run.keyToString(k));
  const orderKeys = tableSettings.columnOrder
    .filter(k => !includes(filteredKeyStrings, k))
    .map(k => Run.keyFromString(k)!);

  const unionedKeys = TableCols.unionKeys(defaultKeys, [
    ...filteredKeys,
    ...orderKeys,
  ]);

  return unionedKeys.map(key => {
    const isDisplayNameCol = Run.keysEqual(key, {
      section: 'run',
      name: 'displayName',
    });

    return TableCols.keyToColumn(key, {
      renderHeader: isDisplayNameCol ? renderHeader : undefined,
      renderCell,
    });
  });
}

function getTags(bulkTagsProject: BulkEditorProjectTagsData | undefined) {
  if (!bulkTagsProject) {
    return [];
  }

  const counts: {[key: string]: number} = {};

  (bulkTagsProject.tagCounts || []).forEach(t => {
    counts[t.name] = t.count;
  });

  return (bulkTagsProject.tags || []).map(tag => ({
    name: tag.name,
    colorIndex: tag.colorIndex,
    count: counts[tag.name] || 0,
  }));
}

export const getRowsMemoized = memoize(getRows);
export const getColumnsMemoized = memoize(getColumns);
export const getTagsMemoized = memoize(getTags);

type RunLike = {id: string};
export const isRunPinned = (
  pinnedRunIds: string[] | undefined,
  run: RunLike
) => (pinnedRunIds ? pinnedRunIds.includes(run.id) : false);
export const isRunBaseline = (
  baselineRunId: string | null | undefined,
  run: RunLike
) => baselineRunId === run.id;
